import { useState } from "react";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import {
  STATUS_TAREFA,
  TIPO_TAREFA_OPTIONS,
  tipoTarefaIcon,
  updateTarefaStatus,
  vencimentoStatus,
} from "../../services/tarefasService";
import { tipoFornecedorIcon } from "../../services/fornecedoresService";
import { formatMoeda } from "../../utils/moeda";
import TarefaFormModal from "../forms/TarefaFormModal";

function formatDateBr(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}`;
}

// Seção expandida dentro do card de Roteiro (RoteirosPage).
// Recebe as tarefas já prontas do DTO do roteiro e delega reload ao pai.
export default function RoteiroTarefasSection({ roteiro, onReload }) {
  const { C } = useTheme();
  const { profile } = useAuth();
  // PR3 moeda-por-guia: símbolo único da moeda do guia. Tarefas são guia-scope,
  // logo t.moeda casa com profile.moeda na prática (TarefaFormModal segue com
  // dropdown próprio — out of scope; campo t.moeda vira vestígio em PR4).
  const moeda = profile?.moeda ?? "EUR";
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const tarefas = roteiro.tarefas ?? [];
  const pendentes = tarefas.filter((t) => t.status === STATUS_TAREFA.Pendente);
  const concluidas = tarefas.filter((t) => t.status === STATUS_TAREFA.Concluida);
  const vencidasCount = pendentes.filter((t) => vencimentoStatus(t) === "vencida").length;

  function openCreate(e) {
    e?.stopPropagation();
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(tarefa, e) {
    e?.stopPropagation();
    setEditing(tarefa);
    setModalOpen(true);
  }

  async function toggleStatus(tarefa, e) {
    e?.stopPropagation();
    if (togglingId === tarefa.id) return;
    const novoStatus =
      tarefa.status === STATUS_TAREFA.Pendente
        ? STATUS_TAREFA.Concluida
        : STATUS_TAREFA.Pendente;
    setTogglingId(tarefa.id);
    try {
      await updateTarefaStatus(roteiro.id, tarefa.id, novoStatus);
      onReload?.();
    } catch {
      // feedback silencioso — recarregar mantém estado consistente
    } finally {
      setTogglingId(null);
    }
  }

  const totalPend = pendentes.length;
  const totalConcl = concluidas.length;

  return (
    <div style={{ marginTop: 18 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
          <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: C.text }}>
            Tarefas operacionais
          </h4>
          <span style={{ fontSize: 11, fontWeight: 600, color: C.textMuted }}>
            {totalPend} pendente{totalPend === 1 ? "" : "s"}
            {totalConcl > 0 && ` · ${totalConcl} concluída${totalConcl === 1 ? "" : "s"}`}
            {vencidasCount > 0 && (
              <>
                {" · "}
                <span style={{ color: C.red }}>
                  {vencidasCount} vencida{vencidasCount === 1 ? "" : "s"}
                </span>
              </>
            )}
          </span>
        </div>
        <button
          onClick={openCreate}
          style={{
            padding: "6px 12px",
            borderRadius: 8,
            border: "none",
            background: C.accent,
            color: "#fff",
            fontSize: 11,
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          + Nova Tarefa
        </button>
      </div>

      {tarefas.length === 0 ? (
        <div
          style={{
            padding: "14px 16px",
            fontSize: 12,
            color: C.textMuted,
            background: C.bg,
            borderRadius: 10,
            border: `1px dashed ${C.border}`,
            textAlign: "center",
          }}
        >
          Nenhuma tarefa ainda. Crie a primeira pra começar a controlar reservas,
          bilhetes e hospedagem deste roteiro.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {tarefas.map((t) => {
            const venc = vencimentoStatus(t);
            const concluida = t.status === STATUS_TAREFA.Concluida;
            const badgeVencColor =
              venc === "vencida"
                ? C.red
                : venc === "proxima"
                ? C.accent
                : venc === "futura"
                ? C.green
                : C.textMuted;

            return (
              <div
                key={t.id}
                onClick={(e) => openEdit(t, e)}
                style={{
                  padding: "10px 14px",
                  background: concluida ? C.greenDim : C.bg,
                  borderRadius: 10,
                  borderLeft: `3px solid ${
                    concluida ? C.green : venc === "vencida" ? C.red : C.accent
                  }`,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  opacity: concluida ? 0.75 : 1,
                  transition: "all 0.15s",
                }}
              >
                {/* Checkbox toggle status */}
                <button
                  type="button"
                  onClick={(e) => toggleStatus(t, e)}
                  disabled={togglingId === t.id}
                  title={concluida ? "Marcar como pendente" : "Marcar como concluída"}
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 6,
                    border: `2px solid ${concluida ? C.green : C.textMuted}`,
                    background: concluida ? C.green : "transparent",
                    color: "#fff",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: togglingId === t.id ? "wait" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    flexShrink: 0,
                  }}
                >
                  {concluida ? "✓" : ""}
                </button>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: concluida ? C.textMuted : C.text,
                      textDecoration: concluida ? "line-through" : "none",
                      marginBottom: 2,
                    }}
                  >
                    <span style={{ marginRight: 6 }}>{tipoTarefaIcon(t.tipo)}</span>
                    {t.titulo}
                  </div>
                  {t.descricao && (
                    <div
                      style={{
                        fontSize: 11,
                        color: C.textMuted,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        marginBottom: 4,
                      }}
                    >
                      {t.descricao}
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      fontSize: 10,
                      fontWeight: 600,
                    }}
                  >
                    {t.dataVencimento && (
                      <span style={{ color: badgeVencColor }}>
                        📅 {formatDateBr(t.dataVencimento)}
                        {venc === "vencida" && " · vencida"}
                        {venc === "proxima" && " · em breve"}
                      </span>
                    )}
                    {t.fornecedorNome && (
                      <span style={{ color: C.textMuted }}>
                        {tipoFornecedorIcon(t.fornecedorTipo)} {t.fornecedorNome}
                      </span>
                    )}
                    {t.valor != null && t.valor > 0 && (
                      <span style={{ color: C.text, fontWeight: 700 }}>
                        💰 {formatMoeda(t.valor, moeda)}
                      </span>
                    )}
                  </div>
                </div>

                <span
                  style={{
                    fontSize: 10,
                    color: C.textDim,
                    fontStyle: "italic",
                    whiteSpace: "nowrap",
                  }}
                >
                  editar
                </span>
              </div>
            );
          })}
        </div>
      )}

      <TarefaFormModal
        open={modalOpen}
        roteiroId={roteiro.id}
        tarefa={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSaved={onReload}
        onDeleted={onReload}
      />
    </div>
  );
}
